import { apiFetch, type OperationResult } from "@/lib/apiClient";
import {
  clearTokens,
  getRefreshToken,
  setRefreshToken,
  setToken,
} from "@/lib/authToken";

export type UserRoleTier = "ADMIN" | "IT_SUPPORT" | "USER";

export const USER_ROLE_TIERS: UserRoleTier[] = ["USER", "IT_SUPPORT", "ADMIN"];

export const USER_ROLE_TIER_LABELS: Record<UserRoleTier, string> = {
  USER: "USER",
  IT_SUPPORT: "IT SUPPORT",
  ADMIN: "ADMIN",
};

export const USER_ROLE_TIER_DESCRIPTIONS: Record<UserRoleTier, string> = {
  USER: "Permissions come from the assigned role.",
  IT_SUPPORT: "Support staff. Permissions come from the assigned role.",
  ADMIN: "Bypasses every permission check.",
};

export function toRoleTier(role: string | null | undefined): UserRoleTier {
  return role === "ADMIN" || role === "IT_SUPPORT" ? role : "USER";
}

export interface SessionUser {
  userId: string;
  email: string;
  name: string;
  role: UserRoleTier;
  userNumber: number | null;
  impersonatedBy?: string | null;
  permissions: string[];
}

interface UserProfileResponse {
  id: string;
  email: string;
  name: string;
  role: string;
  userNumber?: number | null;
  impersonatedBy?: string | null;
  permissions?: string[] | null;
}

interface LoginResponse {
  token: string;
  expiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  user: UserProfileResponse;
}

interface MeResponse {
  id: string;
  email: string;
  name: string;
  role: string;
  userNumber?: number | null;
  impersonatedBy?: string | null;
  permissions?: string[] | null;
}

function toSessionUser(u: UserProfileResponse | MeResponse): SessionUser {
  return {
    userId: u.id,
    email: u.email,
    name: u.name,
    role: toRoleTier(u.role),
    userNumber: u.userNumber ?? null,
    impersonatedBy: u.impersonatedBy ?? null,
    permissions: u.permissions ?? [],
  };
}

async function loginAndStore(path: string, init?: RequestInit): Promise<SessionUser> {
  const res = await apiFetch<LoginResponse>(path, init);
  setToken(res.token);
  if (res.refreshToken) setRefreshToken(res.refreshToken);
  return toSessionUser(res.user);
}

const realAuth = {
  login(identifier: string, password: string) {
    return loginAndStore("/auth/login", {
      method: "POST",
      body: JSON.stringify({ identifier, password }),
    });
  },
  async logout() {
    try {
      const refreshToken = getRefreshToken();
      await apiFetch<void>("/auth/logout", {
        method: "POST",
        body: JSON.stringify({ refreshToken }),
      });
    } finally {
      clearTokens();
    }
  },
  async me() {
    const res = await apiFetch<MeResponse>("/users/me");
    return toSessionUser(res);
  },
  impersonate(userId: string) {
    return loginAndStore(`/auth/impersonate/${encodeURIComponent(userId)}`, {
      method: "POST",
    });
  },
  stopImpersonating() {
    return loginAndStore("/auth/stop-impersonating", { method: "POST" });
  },
};

export const auth = realAuth;

export const passwordReset = {
  validate(token: string) {
    return apiFetch<{ valid: boolean; email?: string; name?: string }>(
      `/public/password-reset/validate?token=${encodeURIComponent(token)}`,
    );
  },
  reset(token: string, newPassword: string) {
    return apiFetch<OperationResult>("/public/password-reset", {
      method: "POST",
      body: JSON.stringify({ token, newPassword }),
    });
  },
};
